import { Component, OnInit, OnDestroy } from '@angular/core';
import { IonicPage, NavParams, AlertController, ToastController } from 'ionic-angular';
import { Store } from '@ngrx/store';
import { Subscription } from 'rxjs';
import { MessageData } from '../../providers/messages';
import { isEmpty, has } from 'lodash';
import { UtilitiesProvider } from '../../providers/utilities';
import { IClientConfig } from '../../models/client.model';
import { IUser } from '../../models/user.model';
import { IAlertMessage, IToastMessage } from '../../models/message.model';
import * as clientSelectors from '../../ngrx/reducers/client.reducer';
import * as userSelectors from '../../ngrx/reducers/auth.reducer';

@IonicPage()
@Component({
  selector: 'page-messages-event',
  templateUrl: 'messages-event.html',
})
export class MessagesEventPage implements OnInit, OnDestroy {

  private clientIdSubs: Subscription;
  private clientId: string;
  private clientInformationSubs: Subscription;
  private clientInformation: IClientConfig;
  private currentAlertMessageSubs: Subscription;
  private currentAlertMessage: IAlertMessage;
  private currentToastMessageSubs: Subscription;
  private currentToastMessage: IToastMessage;
  private userSubs: Subscription;
  private userInformation: IUser;
  private eventId: string;
  private userEventHasViewMessageAlertSubs: Subscription;
  private userEventHasViewMessageToastSubs: Subscription;
  private saveEventStatisticAndViewSubs: Subscription;

  constructor(
    public store: Store<{}>,
    private alertCtrl: AlertController,
    private toastCtrl: ToastController,
    private messageData: MessageData,
    private utilities: UtilitiesProvider,
    private navParams: NavParams
  ) { }

  ngOnInit() {
    this.eventId = this.navParams.get('eventId');
    this.utilities.consoleDecorator('[messages-event] this.eventId', this.eventId, 'red');
    this.getClientId();
    this.getUser();
  }

  private getClientId() {
    this.clientIdSubs = this.store.select(clientSelectors.getClientId).subscribe((clientId) => {
      if (clientId) {
        this.clientId = clientId;
        this.getClientInformation();
        this.getCurrentAlertMessage();
        this.getCurrentToastMessage();
      }
    });
  }

  private getClientInformation() {
    this.clientInformationSubs = this.store.select(clientSelectors.getClientConfig).subscribe((clientConfiguration) => {
      if (clientInformation) {
        this.clientInformation = clientConfiguration;
      }
    });
  }

  private getUser() {
    this.userSubs = this.store.select(userSelectors.getUser).subscribe((user) => {
      this.userInformation = user;
    })
  }

  private getCurrentAlertMessage() {
    this.currentAlertMessageSubs = this.messageData.getEventAlertMessageActive(this.eventId).subscribe((messageActive: IAlertMessage[]) => {
      this.utilities.consoleDecorator('messageActive', messageActive);
      if (!isEmpty(messageActive)) {
        this.currentAlertMessage = messageActive[0];
        this.userHasViewAlertMessage();
      } else {
        this.currentAlertMessage = null;
      }
    })
  }

  private getCurrentToastMessage() {
    this.currentToastMessageSubs = this.messageData.getEventToastMessageActive(this.eventId)
      .subscribe((messageActive: IToastMessage[]) => {
        if (!isEmpty(messageActive)) {
          this.utilities.consoleDecorator('Inspect toast event message', messageActive[0], 'red');
          this.currentToastMessage = messageActive[0];
          this.userHasViewToastMessage();
        } else {
          this.currentToastMessage = null;
        }
      });
  }

  private userHasViewAlertMessage() {
    this.userEventHasViewMessageAlertSubs = this.messageData.userEventHasViewMessage(this.eventId, 'Alert', this.currentAlertMessage.id, this.userInformation.info.id)
      .subscribe((hasViews) => {
        this.utilities.consoleDecorator('hasViews event', hasViews);
        if (!hasViews) {
          this.displayAlertMessage();
        }
      })
  }

  private displayAlertMessage() {
    let alertMessageRecieved = this.alertCtrl.create({
      title: this.currentAlertMessage['title'],
      message: this.currentAlertMessage['text'][this.clientInformation.defaultLanguage],
      buttons: ['Ok']
    });

    alertMessageRecieved.present();
    alertMessageRecieved.onDidDismiss(() => {
      this.messageData.saveEventStatisticAndView(this.eventId, 'Alert', this.currentAlertMessage.id, this.userInformation.info.id).subscribe(() => {
        this.utilities.consoleDecorator('Alert message and statistic saved', '', 'orange');
      })
    })
  }

  private userHasViewToastMessage() {
    this.userEventHasViewMessageToastSubs = this.messageData.userEventHasViewMessage(this.eventId, 'Toast', this.currentToastMessage.id, this.userInformation.info.id)
      .subscribe((hasViews) => {
        this.utilities.consoleDecorator('hasViews event', hasViews);
        if (!hasViews) {
          this.displayToastMessage();
        }
      })
  }

  private displayToastMessage() {
    let alertToastRecieved = this.toastCtrl.create({
      message: this.currentToastMessage['title'],
      duration: this.currentToastMessage.callback ? 0 : this.currentToastMessage.timeout,
      position: this.currentToastMessage.position,
      showCloseButton: true
    });

    let target = "_self";
    if (has(this.currentToastMessage, "is_external")) {
      if (this.currentToastMessage.is_external) {
        target = "_blank"
      }
    }

    alertToastRecieved.present();
    alertToastRecieved.onDidDismiss((data, role) => {
      if (role === 'close') {
        if (this.currentToastMessage.callback !== '') {
          window.open(this.currentToastMessage.callback, target);
        }
      }
      this.saveEventStatisticAndViewSubs = this.messageData.saveEventStatisticAndView(this.eventId, 'Toast', this.currentToastMessage.id, this.userInformation.info.id)
        .subscribe(() => {
          this.utilities.consoleDecorator('Toast message and statistic saved', '', 'orange');
        })
    })
  }

  ngOnDestroy() {
    if (this.clientIdSubs) this.clientIdSubs.unsubscribe();
    if (this.clientInformationSubs) this.clientInformationSubs.unsubscribe();
    if (this.userSubs) this.userSubs.unsubscribe();
    if (this.currentAlertMessageSubs) this.currentAlertMessageSubs.unsubscribe();
    if (this.currentToastMessageSubs) this.currentToastMessageSubs.unsubscribe();
    if (this.userEventHasViewMessageAlertSubs) this.userEventHasViewMessageAlertSubs.unsubscribe();
    if (this.userEventHasViewMessageToastSubs) this.userEventHasViewMessageToastSubs.unsubscribe();
    if (this.saveEventStatisticAndViewSubs) this.saveEventStatisticAndViewSubs.unsubscribe();
  }
}



// WEBPACK FOOTER //
// ./src/pages/messages-event/messages-event.ts